import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  Image,
  useToast,
} from "@chakra-ui/react";
import { Link, useHistory } from "react-router-dom";
import OrgLogo from "assets/img/ORG.png";
import FactsLogo from "assets/img/Facts.jpg";
import SconexLogo from "assets/img/sconex.png";
import { register, getRoles } from "services/authService";
import { getVideoSource } from "services/videoService";
import { LOGIN_BACKGROUND_VIDEO } from "constants/videoAssets";
import "../../styles/auth/SignIn.css";

const isSalesRole = (roleName = "") =>
  roleName.toUpperCase().includes("SALES");

function SignUp() {
  const toast = useToast();
  const history = useHistory();
  const primaryTextColor = "#f8fafc";
  const secondaryTextColor = "rgba(226, 232, 240, 0.85)";
  const focusColor = "#7dd3fc";

  const [formData, setFormData] = useState({
    userName: "",
    email: "",
    password: "",
    roleId: "",
    auditorCode: "",
  });
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadBackground() {
      try {
        const url = await getVideoSource(LOGIN_BACKGROUND_VIDEO);
        if (isMounted) {
          setBackgroundUrl(url);
          setIsVideoReady(true);
        }
      } catch (error) {
        console.error("[SIGN UP] Failed to load background video", error);
        setIsVideoReady(false);
      }
    }

    loadBackground();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadRoles() {
      try {
        const response = await getRoles();
        if (isMounted) {
          setRoles(response.data || []);
        }
      } catch (error) {
        console.error("[SIGN UP] Failed to load roles", error);
        toast({
          title: "Error",
          description: "Failed to load roles. Please refresh the page.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      } finally {
        if (isMounted) setRolesLoading(false);
      }
    }

    loadRoles();

    return () => {
      isMounted = false;
    };

  }, []);

  const selectedRole = roles.find(
    (r) => String(r.roleId) === String(formData.roleId)
  );
  const showAuditorCode = isSalesRole(selectedRole?.roleName);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.userName) newErrors.userName = "Username is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.password) newErrors.password = "Password is required";
    if (!formData.roleId) newErrors.roleId = "Role is required";
    if (showAuditorCode && !formData.auditorCode) {
      newErrors.auditorCode = "Auditor code is required";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        userName: formData.userName,
        email: formData.email,
        password: formData.password,
        role: selectedRole?.roleName,
        roleId: Number(formData.roleId),
      };

      if (showAuditorCode) {
        payload.auditorCode = Number(formData.auditorCode);
      }

      await register(payload);

      toast({
        title: "Account created",
        description: "You can now sign in with your new account.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });

      history.replace("/auth/sign-in");
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to sign up. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex className="signin-shell">
      {backgroundUrl && (
        <video
          className={`signin-bg-video ${isVideoReady ? "is-visible" : ""}`}
          src={backgroundUrl}
          autoPlay
          muted
          loop
          playsInline
        />
      )}
      <Box className="signin-hero">
        <Box className="hero-content">
          <Flex justify="center" align="center" mb="60px">
            <Image
              src={SconexLogo}
              alt="Sconex Logo"
              className="hero-logo"
              draggable="false"
            />
          </Flex>
          <Box position="relative" height="120px">
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite"
            >
              Welcome to ORGI Dashboard
            </Text>
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite 4s"
            >
              Explore Soft Drinks Data
            </Text>
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite 8s"
            >
              Image to Insight – Instantly.
            </Text>
          </Box>
          <Text fontSize="lg" color="rgba(226, 232, 240, 0.92)" mt={8} lineHeight="tall">
            Discover detailed reports, vibrant visuals, and smart analysis all in
            one intelligent dashboard.
          </Text>
        </Box>
      </Box>

      <Box className="signin-panel" color={primaryTextColor}>
        <Flex className="signin-content" align="center" justify="center">
          <Box className={`signin-card${showAuditorCode ? " is-compact" : ""}`}>
            <Box className="card-shimmer" />
            <Stack align="center" spacing={3} mb={showAuditorCode ? 3 : 4}>
              <Flex gap={4} align="center" justify="center">
                <Image
                  src={OrgLogo}
                  alt="ORG Logo"
                  className="signin-logo"
                  draggable="false"
                  background="#fff"
                  borderRadius="5px"
                  padding="5px"
                />
                <Image
                  src={FactsLogo}
                  alt="FACTS Logo"
                  width="90px"
                  height="40px"
                  className="signin-logo"
                  draggable="false"
                />
              </Flex>
              <Text fontSize="2xl" fontWeight="bold" color={primaryTextColor}>
                Create your account
              </Text>
            </Stack>

            <form onSubmit={handleSubmit}>
              <FormControl mb={showAuditorCode ? 3 : 4}>
                <FormLabel color={secondaryTextColor} fontWeight="600">
                  Username
                </FormLabel>
                <Input
                  name="userName"
                  type="text"
                  placeholder="UserName"
                  value={formData.userName}
                  onChange={handleChange}
                  className="signin-input"
                  _focus={{
                    borderColor: focusColor,
                    boxShadow: `0 0 0 1px ${focusColor}`,
                  }}
                />
                {errors.userName && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {errors.userName}
                  </Text>
                )}
              </FormControl>

              <FormControl mb={showAuditorCode ? 3 : 4}>
                <FormLabel color={secondaryTextColor} fontWeight="600">
                  Email
                </FormLabel>
                <Input
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  className="signin-input"
                  _focus={{
                    borderColor: focusColor,
                    boxShadow: `0 0 0 1px ${focusColor}`,
                  }}
                />
                {errors.email && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {errors.email}
                  </Text>
                )}
              </FormControl>

              <FormControl mb={showAuditorCode ? 3 : 4}>
                <FormLabel color={secondaryTextColor} fontWeight="600">
                  Password
                </FormLabel>
                <Input
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className="signin-input"
                  _focus={{
                    borderColor: focusColor,
                    boxShadow: `0 0 0 1px ${focusColor}`,
                  }}
                />
                {errors.password && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {errors.password}
                  </Text>
                )}
              </FormControl>


              <FormControl mb={showAuditorCode ? 3 : 4}>
                <FormLabel color={secondaryTextColor} fontWeight="600">
                  Role
                </FormLabel>
                <Select
                  name="roleId"
                  placeholder={
                    rolesLoading ? "Loading roles..." : "Select a role"
                  }
                  value={formData.roleId}
                  className="signin-input signin-select"
                  onChange={handleChange}
                  isDisabled={rolesLoading}
                  _focus={{
                    borderColor: focusColor,
                    boxShadow: `0 0 0 1px ${focusColor}`,
                  }}
                >
                  {roles.map((role) => (
                    <option
                      key={role.roleId}
                      value={role.roleId}
                      style={{ color: "#000" }}
                    >
                      {role.roleName}
                    </option>
                  ))}
                </Select>
                {errors.roleId && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {errors.roleId}
                  </Text>
                )}
              </FormControl>

              {showAuditorCode && (
                <FormControl mb={3}>
                  <FormLabel color={secondaryTextColor} fontWeight="600">
                    Auditor Code
                  </FormLabel>
                  <Input
                    name="auditorCode"
                    type="number"
                    placeholder="Enter auditor code"
                    value={formData.auditorCode}
                    onChange={handleChange}
                    className="signin-input"
                    _focus={{
                      borderColor: focusColor,
                      boxShadow: `0 0 0 1px ${focusColor}`,
                    }}
                  />
                  {errors.auditorCode && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      {errors.auditorCode}
                    </Text>
                  )}
                </FormControl>
              )}

              <Flex justify="flex-end" align="center" mb={showAuditorCode ? 4 : 5}>
                <Text fontSize="sm" color={secondaryTextColor}>
                  Already have an account?{" "}
                  <Link
                    to="/auth/sign-in"
                    style={{ color: focusColor, fontWeight: "600" }}
                  >
                    Sign in
                  </Link>
                </Text>
              </Flex>

              <Button
                type="submit"
                width="100%"
                className="signin-button"
                py={showAuditorCode ? 3 : 4}
                isLoading={submitting}
                bgGradient="linear(140deg, #111111 0%, #000000 55%, #1a1a1a 100%)"
                color="white"
                _hover={{
                  bgGradient:
                    "linear(140deg, #050505 0%, #000000 60%, #111111 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 25px 45px rgba(0, 0, 0, 0.45)",
                }}
                _active={{
                  bgGradient:
                    "linear(140deg, #050505 0%, #0a0a0a 55%, #111111 100%)",
                  transform: "translateY(0)",
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.35)",
                }}
              >
                Sign Up
              </Button>
            </form>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
}

export default SignUp;